import React, { useEffect, useRef, useState } from "react";
import Icon from "../../atoms/Icon/Icon.component";
import {
  AssistantRow,
  AssistantSlot,
  AssistantStamp,
  Avatar,
  Card,
  ChartBar,
  ChartBarLabel,
  ChartBarLabels,
  ChartBars,
  ChartCaption,
  ChartRow,
  Chip,
  Chrome,
  Dot,
  Glyph,
  Header,
  HeaderTitle,
  InputBar,
  InputField,
  InputText,
  Messages,
  Panel,
  Paragraph,
  SendCircle,
  Tab,
  Tabs,
  Thinking,
  TodayDivider,
  Toolbar,
  TypeGhost,
  TypeReveal,
  TypeWrap,
  UserBubble,
  UserRow,
  UserStamp,
} from "./AnalystDemo.styled";

/* ---------------------------------------------------------------------------
 * Demo content - supplied by Tendbe.
 * Nothing here may be invented: this panel is a claim about the product.
 * ------------------------------------------------------------------------- */
const DEMO_EVENT_NAME = "Your Event";
const DEMO_QUESTION = "What should we repeat and improve across our events?";
const DEMO_ANSWER_PARAGRAPHS = [
  "Repeat the networking format, your highest-rated element two events running.",
  "Improve session length — it's your most common complaint and has increased since the previous event.",
];
const DEMO_CHART_CAPTION = "Satisfaction across your last 3 events";
/** Relative bar heights, 0-1. Illustrative only - no numbers are shown. */
const DEMO_CHART_BARS = [0.55, 0.7, 1.0];
/**
 * Shown under every message, as production does. Kept generic rather than
 * inventing a specific clock time - this demo isn't tied to a real event.
 */
const DEMO_TIMESTAMP = "Just now";

/**
 * Longest question that still fits on one line in the hero panel. Anything
 * longer falls back to a wrapped bubble that fades in, because the
 * single-line steps() typewriter cannot wrap without truncating.
 */
const MAX_TYPEWRITER_CHARS = 60;

/**
 * How often the whole sequence replays while the panel is visible, in ms.
 * A fixed period between restarts (not "N ms after it finishes"), so the
 * hold at the end simply shrinks or grows to fill whatever time is left
 * before the next tick.
 */
const REPLAY_INTERVAL_MS = 60_000;

/** Sequence timings in seconds. One run takes ~6s, see REPLAY_INTERVAL_MS. */
const TIMELINE = {
  question: 0.3,
  typingDuration: 1.6,
  /** Caret blinks across exactly the typing window, then disappears. */
  caretBlinks: 4,
  questionStamp: 1.9,
  dotsIn: 2.1,
  dotsOut: 3.0,
  answerCard: 3.1,
  paragraphStagger: 0.4,
  chart: 5.0,
  /** Floor only - grows to clear the bars if there are more of them, or
   *  they're staggered further apart, than today. See finalStampDelay. */
  finalStamp: 5.6,
};

/** Delay between each bar's grow-in, left to right - same cadence as the
 *  thinking dots' pulse stagger, just applied to height instead of opacity. */
const CHART_BAR_STAGGER = 0.15;
const CHART_BAR_GROW_DURATION = 0.5;

const seconds = (value: number) => `${value}s`;

/** Inline custom properties feed the delays into the CSS keyframes. */
const delay = (value: number) =>
  ({ "--enter": seconds(value) } as React.CSSProperties);

const ARIA_LABEL =
  `Tendbe AI Analyst for ${DEMO_EVENT_NAME}: the question ` +
  `"${DEMO_QUESTION}" is answered by the analyst, which also shows a chart ` +
  `titled "${DEMO_CHART_CAPTION}".`;

const CollectGlyph = () => (
  <Glyph
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M8 16v-4M12 16V8M16 16v-6" />
  </Glyph>
);

const ReportsGlyph = () => (
  <Glyph
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 16l5-5 3 3 6-7" />
    <path d="M19 3v3M17.5 4.5h3" />
  </Glyph>
);

const ChevronGlyph = () => (
  <Glyph
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </Glyph>
);

const SendGlyph = () => (
  <Glyph
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m5 12 15-7-7 15-2.5-6.5L5 12Z" />
  </Glyph>
);

/**
 * Animated replica of Tendbe's AI Analyst, for the landing hero.
 *
 * Animated DOM only - CSS keyframes driven by React state, no canvas, no
 * animation library, no media. The sequence starts when the panel scrolls
 * into view, then replays every REPLAY_INTERVAL_MS for as long as the
 * panel stays on screen - it holds on the final frame between runs rather
 * than looping continuously, and skips a replay entirely while the panel
 * is scrolled out of view. It is decorative: the panel is exposed to
 * assistive technology as a single labelled image and contains no
 * focusable or interactive elements.
 */
const AnalystDemo = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  /** Bumped to remount the animated subtree below, which replays every
   *  CSS animation from its 0% keyframe - simpler and more reliable than
   *  toggling `hasStarted` off and on to re-trigger the same elements. */
  const [replayKey, setReplayKey] = useState(0);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setHasStarted(true);
      return undefined;
    }

    // No IntersectionObserver support: play once, immediately, and treat
    // the panel as always "in view" so the replay loop below still runs.
    let isInView = typeof IntersectionObserver === "undefined";
    let observer: IntersectionObserver | undefined;

    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          isInView = entry.isIntersecting;
          if (entry.isIntersecting) {
            setHasStarted(true);
          }
        },
        { threshold: 0.3 }
      );
      observer.observe(node);
    } else {
      setHasStarted(true);
    }

    const replayTimer = window.setInterval(() => {
      if (isInView) {
        setReplayKey((key) => key + 1);
      }
    }, REPLAY_INTERVAL_MS);

    return () => {
      observer?.disconnect();
      window.clearInterval(replayTimer);
    };
  }, []);

  const canType = DEMO_QUESTION.length <= MAX_TYPEWRITER_CHARS;
  const lastParagraph =
    TIMELINE.answerCard +
    Math.max(DEMO_ANSWER_PARAGRAPHS.length - 1, 0) * TIMELINE.paragraphStagger;
  const chartDelay = Math.max(TIMELINE.chart, lastParagraph + 0.7);
  const maxBarIndex = DEMO_CHART_BARS.indexOf(Math.max(...DEMO_CHART_BARS));
  const lastBarGrowEnd =
    chartDelay +
    Math.max(DEMO_CHART_BARS.length - 1, 0) * CHART_BAR_STAGGER +
    CHART_BAR_GROW_DURATION;
  /** Waits for the last bar to finish growing before the final timestamp
   *  appears, so it never lands mid-animation. */
  const finalStampDelay = Math.max(TIMELINE.finalStamp, lastBarGrowEnd + 0.2);

  return (
    <Panel ref={rootRef} role="img" aria-label={ARIA_LABEL}>
      <Chrome aria-hidden="true">
        <Header>
          <HeaderTitle>{DEMO_EVENT_NAME}</HeaderTitle>
        </Header>

        <Tabs>
          <Tab>
            <CollectGlyph />
            Collect
          </Tab>
          <Tab>
            <ReportsGlyph />
            Reports
          </Tab>
          <Tab $active>
            <Icon name="analysis" />
            AI Analyst
          </Tab>
        </Tabs>

        <Toolbar>
          <Chip>
            Add Events
            <ChevronGlyph />
          </Chip>
        </Toolbar>

        <Messages key={replayKey}>
          <TodayDivider>Today</TodayDivider>

          <UserRow>
            <UserBubble $play={hasStarted} style={delay(TIMELINE.question)}>
              {canType ? (
                <TypeWrap>
                  <TypeGhost>{DEMO_QUESTION}</TypeGhost>
                  <TypeReveal
                    $play={hasStarted}
                    style={
                      {
                        "--enter": seconds(TIMELINE.question),
                        "--type-duration": seconds(TIMELINE.typingDuration),
                        "--chars": DEMO_QUESTION.length,
                        "--caret-blinks": TIMELINE.caretBlinks,
                        "--caret-period": seconds(
                          TIMELINE.typingDuration / TIMELINE.caretBlinks
                        ),
                      } as React.CSSProperties
                    }
                  >
                    {DEMO_QUESTION}
                  </TypeReveal>
                </TypeWrap>
              ) : (
                DEMO_QUESTION
              )}
            </UserBubble>
            <UserStamp
              $play={hasStarted}
              style={delay(TIMELINE.questionStamp)}
            >
              {DEMO_TIMESTAMP} ✓
            </UserStamp>
          </UserRow>

          <AssistantSlot>
            <Thinking
              $play={hasStarted}
              style={
                {
                  "--dots-in": seconds(TIMELINE.dotsIn),
                  "--dots-out": seconds(TIMELINE.dotsOut),
                } as React.CSSProperties
              }
            >
              {[0, 1, 2].map((index) => (
                <Dot
                  key={index}
                  $play={hasStarted}
                  style={
                    {
                      "--dot-delay": seconds(TIMELINE.dotsIn + index * 0.15),
                    } as React.CSSProperties
                  }
                />
              ))}
            </Thinking>

            <AssistantRow
              $play={hasStarted}
              style={delay(TIMELINE.answerCard)}
            >
              <Avatar>
                <Icon name="analysis" />
              </Avatar>
              <Card>
                {DEMO_ANSWER_PARAGRAPHS.map((paragraph, index) => (
                  <Paragraph
                    key={`${paragraph}-${index}`}
                    $play={hasStarted}
                    style={delay(
                      TIMELINE.answerCard + index * TIMELINE.paragraphStagger
                    )}
                  >
                    {paragraph}
                  </Paragraph>
                ))}
              </Card>
            </AssistantRow>
          </AssistantSlot>

          <ChartRow $play={hasStarted} style={delay(chartDelay)}>
            <Avatar>
              <Icon name="analysis" />
            </Avatar>
            <Card>
              <ChartCaption $play={hasStarted} style={delay(chartDelay)}>
                {DEMO_CHART_CAPTION}
              </ChartCaption>
              <ChartBars>
                {DEMO_CHART_BARS.map((value, index) => (
                  <ChartBar
                    key={index}
                    $play={hasStarted}
                    $accent={index === maxBarIndex}
                    style={{
                      height: `${value * 100}%`,
                      ...delay(chartDelay + index * CHART_BAR_STAGGER),
                    }}
                  />
                ))}
              </ChartBars>
              <ChartBarLabels>
                {DEMO_CHART_BARS.map((_, index) => (
                  <ChartBarLabel key={index}>Event {index + 1}</ChartBarLabel>
                ))}
              </ChartBarLabels>
            </Card>
          </ChartRow>

          <AssistantStamp
            $play={hasStarted}
            style={delay(finalStampDelay)}
          >
            {DEMO_TIMESTAMP}
          </AssistantStamp>
        </Messages>

        <InputBar>
          <InputField>
            <InputText>Ask about your event insights...</InputText>
            <SendCircle>
              <SendGlyph />
            </SendCircle>
          </InputField>
        </InputBar>
      </Chrome>
    </Panel>
  );
};

export default AnalystDemo;
